import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faToolbox, 
  faComments, 
  faUserMd, 
  faMagic, 
  faHistory, 
  faChartBar,
  faPlay,
  faMicrophone,
  faVolumeUp,
  faUsers,
  faDownload,
  faCheck,
  faTimes,
  faSpinner,
  faInfoCircle,
  faExclamationTriangle,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import { toolboxAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { OpinionModal } from '../shared/OpinionModal';
import './Toolbox.css';

interface Answer {
  type: 'rag' | 'basic';
  content: string;
  has_sources: boolean;
  // GPT-OSS advanced features
  thinking_process?: string | null;
  web_search_queries?: string[] | null;
  web_search_results?: string[] | null;
  tool_calls?: string[] | null;
  confidence_level?: string | null;
}

interface ChatResponse {
  session_id: string;
  answers: Answer[];
  response_time_ms: number;
  rag_sources?: string;  // JSON array of sources from AI Consilium
}

interface Source {
  id: number;
  title: string;
}

// Helper to parse JSON sources, with fallback for legacy format
const parseSources = (sourcesJson: string | undefined): Source[] => {
  if (!sourcesJson) return [];
  try {
    const parsed = JSON.parse(sourcesJson);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    // Legacy format or invalid JSON - return empty
    return [];
  }
};

interface ClinicalImprovementResponse {
  session_id: string;
  improved_context: string;
  original_context: string;
  response_time_ms: number;
}

interface Patient {
  id: number;
  name: string;
  email: string;
  has_clinical_info: boolean;
}

interface ToolboxHistory {
  id: number;
  session_id: string;
  tool_type: string;
  question: string;
  enhanced_question?: string;
  clinical_context: string;
  rag_answer: string;
  basic_answer: string;
  selected_answer?: string;
  ai_provider: string;
  response_time_ms: number;
  created_at: string;
  patient_name?: string;
  patient_email?: string;
  rag_prompt?: string;
  basic_prompt?: string;
  additional_data?: any; // includes rag_sources_preview, feedback, etc.
  feedback?: string;  // legacy top-level feedback (fallback)
}

interface ToolboxStatistics {
  total_sessions: number;
  tool_type_breakdown: Record<string, number>;
  answer_preferences: Record<string, number>;
  average_response_time_ms: number;
  top_users: Array<{
    user_id: number;
    name: string;
    email: string;
    usage_count: number;
  }>;
  period_days: number;
}

type ActiveTab = 'ai-chat' | 'clinical-improvement' | 'history' | 'statistics';

export const Toolbox: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('ai-chat');
  
  // AI Chat state
  const [question, setQuestion] = useState('');
  const [clinicalContext, setClinicalContext] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);
  const [chatResponse, setChatResponse] = useState<ChatResponse | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  
  // Advanced features state
  const [showRagAdvancedFeatures, setShowRagAdvancedFeatures] = useState(false);
  const [showBasicAdvancedFeatures, setShowBasicAdvancedFeatures] = useState(false);
  
  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [ragStreamContent, setRagStreamContent] = useState('');
  const [basicStreamContent, setBasicStreamContent] = useState('');
  const [streamingProgress, setStreamingProgress] = useState(0);
  const [streamingStage, setStreamingStage] = useState('');
  const [useStreaming, setUseStreaming] = useState(
    localStorage.getItem('toolbox-use-streaming') ? 
    JSON.parse(localStorage.getItem('toolbox-use-streaming')!) : true
  );
  
  // Clinical Improvement state
  const [currentContext, setCurrentContext] = useState('');
  const [improvementResponse, setImprovementResponse] = useState<ClinicalImprovementResponse | null>(null);
  const [improvementLoading, setImprovementLoading] = useState(false);
  
  // Feedback state
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSessionId, setFeedbackSessionId] = useState<string | null>(null);
  
  // Shared state
  const [patients, setPatients] = useState<Patient[]>([]);
  const [error, setError] = useState('');
  const [history, setHistory] = useState<ToolboxHistory[]>([]);
  const [statistics, setStatistics] = useState<ToolboxStatistics | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  
  // Voice control state
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);

  const loadPatients = useCallback(async () => {
    try {
      const patientsData = await toolboxAPI.getMyPatients();
      setPatients(patientsData);
    } catch (error) {
      console.error('Error loading patients:', error);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      console.log('Loading history...');
      const historyData = await toolboxAPI.getHistory(30);
      console.log('History data received:', historyData);
      setHistory(historyData.history || []);
    } catch (error) {
      console.error('Error loading history:', error);
      setError('Błąd podczas ładowania historii');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadStatistics = useCallback(async () => {
    setStatisticsLoading(true);
    try {
      const statsData = await toolboxAPI.getStatistics(30);
      setStatistics(statsData);
    } catch (error) {
      console.error('Error loading statistics:', error);
      setError('Błąd podczas ładowania statystyk');
    } finally {
      setStatisticsLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('useEffect triggered, activeTab:', activeTab);
    loadPatients();
    if (activeTab === 'history') {
      console.log('Loading history due to tab change...');
      loadHistory();
    }
    if (activeTab === 'statistics') {
      console.log('Loading statistics due to tab change...');
      loadStatistics();
    }
    
    // Check speech support
    setSpeechSupported(
      'webkitSpeechRecognition' in window || 'SpeechRecognition' in window
    );
  }, [activeTab, loadPatients, loadHistory, loadStatistics]);

  const handlePatientSelect = async (patientId: number) => {
    try {
      const patientContext = await toolboxAPI.getPatientClinicalContext(patientId);
      setClinicalContext(patientContext.clinical_context);
      setSelectedPatientId(patientId);
    } catch (error) {
      console.error('Error loading patient context:', error);
      setError('Błąd podczas ładowania kontekstu pacjenta');
    }
  };

  const handleAIChat = async () => {
    if (!question.trim() || !clinicalContext.trim()) {
      setError('Proszę wprowadzić pytanie i kontekst kliniczny');
      return;
    }

    setChatLoading(true);
    setError('');
    setChatResponse(null);
    setSelectedAnswer(null);
    
    // Reset streaming state
    setRagStreamContent('');
    setBasicStreamContent('');
    setStreamingProgress(0);
    setStreamingStage('');

    try {
      if (useStreaming) {
        setIsStreaming(true);
        
        const response = await toolboxAPI.chatStreaming(
          question.trim(),
          clinicalContext.trim(),
          selectedPatientId || undefined,
          (progress) => setStreamingProgress(progress),
          (chunk) => setRagStreamContent(prev => prev + chunk),
          (chunk) => setBasicStreamContent(prev => prev + chunk),
          (stage, progress) => {
            setStreamingStage(stage);
            if (progress !== undefined) {
              setStreamingProgress(progress);
            }
          }
        );
        
        setChatResponse(response as ChatResponse);
      } else {
        // Fallback to regular API
        const response = await toolboxAPI.chat(
          question.trim(),
          clinicalContext.trim(),
          selectedPatientId || undefined
        );
        setChatResponse(response);
      }
      
      // Reload history to show the new chat entry
      loadHistory();
    } catch (error) {
      console.error('Error in AI chat:', error);
      setError('Błąd podczas przetwarzania zapytania AI');
    } finally {
      setChatLoading(false);
      setIsStreaming(false);
    }
  };

  const handleSelectAnswer = async (answerType: 'rag' | 'basic') => {
    if (!chatResponse?.session_id) return;

    try {
      await toolboxAPI.selectAnswer(chatResponse.session_id, answerType);
      setSelectedAnswer(answerType);
      // Show feedback option after answer selection
      setFeedbackSessionId(chatResponse.session_id);
      setShowFeedback(true);
    } catch (error) {
      console.error('Error selecting answer:', error);
    }
  };

  const handleSubmitFeedback = async (feedbackText: string) => {
    if (!feedbackSessionId) return;

    try {
      await toolboxAPI.submitFeedback(feedbackSessionId, feedbackText);
      setShowFeedback(false);
      setFeedbackSessionId(null);
      // Reload history to show updated feedback
      if (activeTab === 'history') {
        loadHistory();
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error; // Re-throw to let modal handle the error
    }
  };

  const handleSkipFeedback = () => {
    setShowFeedback(false);
    setFeedbackSessionId(null);
  };

  const handleClinicalImprovement = async () => {
    if (!currentContext.trim()) {
      setError('Proszę wprowadzić kontekst kliniczny do ulepszenia');
      return;
    }

    setImprovementLoading(true);
    setError('');
    setImprovementResponse(null);

    try {
      const response = await toolboxAPI.improveClinicalContext(
        currentContext.trim()
      );
      setImprovementResponse(response);
      // Reload history to show the new improvement entry
      loadHistory();
    } catch (error) {
      console.error('Error in clinical improvement:', error);
      setError('Błąd podczas przetwarzania ulepszenia kontekstu');
    } finally {
      setImprovementLoading(false);
    }
  };

  const speakText = (text: string) => {
    if (!('speechSynthesis' in window)) return;

    stopSpeaking();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pl-PL';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  const startListening = () => {
    if (!speechSupported) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.lang = 'pl-PL';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      if (activeTab === 'ai-chat') {
        setQuestion(transcript);
      } else if (activeTab === 'clinical-improvement') {
        setCurrentContext(transcript);
      }
    };

    recognition.start();
  };

  const resetChatForm = () => {
    setQuestion('');
    setClinicalContext('');
    setSelectedPatientId(null);
    setChatResponse(null);
    setSelectedAnswer(null);
    setError('');
  };

  const resetImprovementForm = () => {
    setCurrentContext('');
    setImprovementResponse(null);
    setError('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ragAnswer = chatResponse?.answers.find(a => a.type === 'rag');
  const basicAnswer = chatResponse?.answers.find(a => a.type === 'basic');

  return (
    <div className="toolbox-container">
      <div className="toolbox-header">
        <h1><FontAwesomeIcon icon={faToolbox} /> Narzędzia AI</h1>
        <p>Zaawansowane narzędzia AI dla lekarzy i administratorów</p>
      </div>

      {error && (
        <div className="error-message" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error}
        </div>
      )}

      <div className="toolbox-tabs">
        <button
          className={`tab-button ${activeTab === 'ai-chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai-chat')}
        >
          <FontAwesomeIcon icon={faComments} />
          Chat AI
        </button>
        <button
          className={`tab-button ${activeTab === 'clinical-improvement' ? 'active' : ''}`}
          onClick={() => setActiveTab('clinical-improvement')}
        >
          <FontAwesomeIcon icon={faMagic} />
          Ulepszanie kontekstu
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <FontAwesomeIcon icon={faHistory} />
          Historia
        </button>
        {user?.is_admin && (
          <button
            className={`tab-button ${activeTab === 'statistics' ? 'active' : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            <FontAwesomeIcon icon={faChartBar} />
            Statystyki
          </button>
        )}
      </div>

      {/* AI Chat Tab */}
      {activeTab === 'ai-chat' && (
        <div className="tab-content">
          <div className="toolbox-section">
            <div className="section-header">
              <div className="section-title">
                <h3><FontAwesomeIcon icon={faComments} /> Chat AI z podwójnymi odpowiedziami</h3>
                <p>Zadawaj pytania medyczne z własnym kontekstem klinicznym</p>
              </div>
              <div className="section-controls">
                <label className="streaming-toggle">
                  <input
                    type="checkbox"
                    checked={useStreaming}
                    onChange={(e) => {
                      setUseStreaming(e.target.checked);
                      localStorage.setItem('toolbox-use-streaming', JSON.stringify(e.target.checked));
                    }}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label">Odpowiedź na żywo</span>
                </label>
              </div>
            </div>

            {/* Patient Selection */}
            <div className="form-group">
              <label><FontAwesomeIcon icon={faUsers} /> Wybierz pacjenta (opcjonalnie):</label>
              <select
                value={selectedPatientId || ''}
                onChange={(e) => {
                  const patientId = e.target.value ? parseInt(e.target.value) : null;
                  if (patientId) {
                    handlePatientSelect(patientId);
                  } else {
                    setSelectedPatientId(null);
                    setClinicalContext('');
                  }
                }}
              >
                <option value="">-- Lub wprowadź własny kontekst --</option>
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name} ({patient.email})
                    {!patient.has_clinical_info && ' - Brak info klinicznego'}
                  </option>
                ))}
              </select>
            </div>

            {/* Clinical Context */}
            <div className="form-group">
              <label><FontAwesomeIcon icon={faUserMd} /> Kontekst kliniczny:</label>
              <textarea
                value={clinicalContext}
                onChange={(e) => setClinicalContext(e.target.value)}
                placeholder="Wprowadź szczegółowy kontekst kliniczny pacjenta..."
                rows={4}
                disabled={chatLoading}
              />
            </div>

            {/* Question */}
            <div className="form-group">
              <label>Pytanie:</label>
              <div className="input-with-voice">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Zadaj pytanie medyczne..."
                  rows={3}
                  disabled={chatLoading}
                />
                {speechSupported && (
                  <button
                    type="button"
                    className={`voice-button ${isListening ? 'listening' : ''}`}
                    onClick={startListening}
                    disabled={chatLoading || isListening}
                    title="Nagraj pytanie głosem"
                  >
                    <FontAwesomeIcon icon={faMicrophone} />
                  </button>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                onClick={handleAIChat}
                disabled={chatLoading || !question.trim() || !clinicalContext.trim()}
                className="primary-button"
              >
                {chatLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Przetwarzanie...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} />
                    Zadaj pytanie
                  </>
                )}
              </button>
              <button
                onClick={resetChatForm}
                disabled={chatLoading}
                className="secondary-button"
              >
                <FontAwesomeIcon icon={faTimes} />
                Wyczyść
              </button>
            </div>

            {/* Streaming Progress */}
            {(chatLoading || isStreaming) && (
              <div className="streaming-container">
                <h4>
                  {isStreaming ? 'Generowanie odpowiedzi w czasie rzeczywistym...' : 'Przetwarzanie zapytania...'}
                </h4>
                
                {/* Progress Bar */}
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${streamingProgress}%` }}></div>
                </div>
                <div className="progress-info">
                  <span>{Math.round(streamingProgress)}%</span>
                  <span>{streamingStage}</span>
                </div>

                {isStreaming && (
                  <div className="streaming-responses">
                    <div className="responses-grid">
                      {/* Streaming RAG Answer */}
                      <div className="response-card streaming">
                        <div className="response-header">
                          <h5>
                            Odpowiedź RAG <span className="streaming-indicator">⚡ Streaming</span>
                          </h5>
                          <div className="typing-indicator">
                            <FontAwesomeIcon icon={faPaperPlane} className="typing-icon" />
                          </div>
                        </div>
                        <div className="response-content streaming-content">
                          <ReactMarkdown components={{
                            p: ({children}) => <p>{children}</p>,
                            br: () => <br />,
                          }}>
                            {ragStreamContent || "Rozpoczynam generowanie odpowiedzi..."}
                          </ReactMarkdown>
                          {ragStreamContent && <span className="streaming-cursor">|</span>}
                        </div>
                      </div>

                      {/* Streaming Basic Answer */}
                      <div className="response-card streaming">
                        <div className="response-header">
                          <h5>
                            Odpowiedź podstawowa <span className="streaming-indicator">⚡ Streaming</span>
                          </h5>
                          <div className="typing-indicator">
                            <FontAwesomeIcon icon={faPaperPlane} className="typing-icon" />
                          </div>
                        </div>
                        <div className="response-content streaming-content">
                          <ReactMarkdown components={{
                            p: ({children}) => <p>{children}</p>,
                            br: () => <br />,
                          }}>
                            {basicStreamContent || "Rozpoczynam generowanie odpowiedzi..."}
                          </ReactMarkdown>
                          {basicStreamContent && <span className="streaming-cursor">|</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Chat Response */}
            {chatResponse && (
              <div className="chat-responses">
                <h4>Odpowiedzi AI:</h4>
                <div className="responses-grid">
                  {/* RAG Answer */}
                  <div className={`response-card ${selectedAnswer === 'rag' ? 'selected' : ''}`}>
                    <div className="response-header">
                      <h5>
                        Odpowiedź RAG 
                        {ragAnswer?.has_sources && <span className="sources-badge">Ze źródłami</span>}
                      </h5>
                      <div className="response-actions">
                        <button
                          onClick={() => speakText(ragAnswer?.content || '')}
                          disabled={isSpeaking}
                          className="speak-button"
                          title="Odczytaj odpowiedź"
                        >
                          <FontAwesomeIcon icon={faVolumeUp} />
                        </button>
                        <button
                          onClick={() => handleSelectAnswer('rag')}
                          disabled={selectedAnswer !== null}
                          className={`select-button ${selectedAnswer === 'rag' ? 'selected' : ''}`}
                        >
                          {selectedAnswer === 'rag' ? (
                            <FontAwesomeIcon icon={faCheck} />
                          ) : (
                            'Wybierz'
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="response-content">
                      <ReactMarkdown 
                        components={{
                          p: ({children}) => <p>{children}</p>,
                          br: () => <br />,
                        }}
                      >
                        {ragAnswer?.content || ''}
                      </ReactMarkdown>
                    </div>
                    
                    {/* GPT-OSS Advanced Features for RAG */}
                    {(ragAnswer?.thinking_process || ragAnswer?.web_search_queries || ragAnswer?.tool_calls) && (
                      <div className="advanced-features">
                        <div className="features-header">
                          <h4>🔬 Zaawansowane funkcje</h4>
                        </div>
                        
                        {ragAnswer?.thinking_process && (
                          <div className="feature-section">
                            <button 
                              className={`feature-toggle ${showRagAdvancedFeatures ? 'expanded' : ''}`}
                              onClick={() => setShowRagAdvancedFeatures(!showRagAdvancedFeatures)}
                            >
                              🧠 Proces myślenia
                              {showRagAdvancedFeatures ? '▼' : '▶'}
                            </button>
                            {showRagAdvancedFeatures && (
                              <div className="feature-content thinking-content">
                                <pre>{ragAnswer.thinking_process}</pre>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {ragAnswer?.web_search_queries && ragAnswer.web_search_queries.length > 0 && (
                          <div className="feature-section">
                            <button 
                              className={`feature-toggle ${showRagAdvancedFeatures ? 'expanded' : ''}`}
                              onClick={() => setShowRagAdvancedFeatures(!showRagAdvancedFeatures)}
                            >
                              🌐 Wyszukiwanie internetowe
                              {showRagAdvancedFeatures ? '▼' : '▶'}
                            </button>
                            {showRagAdvancedFeatures && (
                              <div className="feature-content web-search-content">
                                <div className="search-queries">
                                  <h5>Zapytania:</h5>
                                  <ul>
                                    {ragAnswer.web_search_queries.map((query, index) => (
                                      <li key={index}>{query}</li>
                                    ))}
                                  </ul>
                                </div>
                                {ragAnswer?.web_search_results && ragAnswer.web_search_results.length > 0 && (
                                  <div className="search-results">
                                    <h5>Wyniki:</h5>
                                    <ul>
                                      {ragAnswer.web_search_results.map((result, index) => (
                                        <li key={index}>{result}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {ragAnswer?.tool_calls && ragAnswer.tool_calls.length > 0 && (
                          <div className="feature-section">
                            <button 
                              className={`feature-toggle ${showRagAdvancedFeatures ? 'expanded' : ''}`}
                              onClick={() => setShowRagAdvancedFeatures(!showRagAdvancedFeatures)}
                            >
                              🔧 Wywołania narzędzi
                              {showRagAdvancedFeatures ? '▼' : '▶'}
                            </button>
                            {showRagAdvancedFeatures && (
                              <div className="feature-content tool-calls-content">
                                <ul>
                                  {ragAnswer.tool_calls.map((call, index) => (
                                    <li key={index}>{call}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {ragAnswer?.confidence_level && (
                          <div className="confidence-indicator">
                            <span className="confidence-label">📊 Poziom pewności:</span>
                            <span className="confidence-value">{ragAnswer.confidence_level}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Basic Answer */}
                  <div className={`response-card ${selectedAnswer === 'basic' ? 'selected' : ''}`}>
                    <div className="response-header">
                      <h5>Odpowiedź AI</h5>
                      <div className="response-actions">
                        <button
                          onClick={() => speakText(basicAnswer?.content || '')}
                          disabled={isSpeaking}
                          className="speak-button"
                          title="Odczytaj odpowiedź"
                        >
                          <FontAwesomeIcon icon={faVolumeUp} />
                        </button>
                        <button
                          onClick={() => handleSelectAnswer('basic')}
                          disabled={selectedAnswer !== null}
                          className={`select-button ${selectedAnswer === 'basic' ? 'selected' : ''}`}
                        >
                          {selectedAnswer === 'basic' ? (
                            <FontAwesomeIcon icon={faCheck} />
                          ) : (
                            'Wybierz'
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="response-content">
                      <ReactMarkdown 
                        components={{
                          p: ({children}) => <p>{children}</p>,
                          br: () => <br />,
                        }}
                      >
                        {basicAnswer?.content || ''}
                      </ReactMarkdown>
                    </div>
                    
                    {/* GPT-OSS Advanced Features for Basic */}
                    {(basicAnswer?.thinking_process || basicAnswer?.web_search_queries || basicAnswer?.tool_calls) && (
                      <div className="advanced-features">
                        <div className="features-header">
                          <h4>🔬 Zaawansowane funkcje</h4>
                        </div>
                        
                        {basicAnswer?.thinking_process && (
                          <div className="feature-section">
                            <button 
                              className={`feature-toggle ${showBasicAdvancedFeatures ? 'expanded' : ''}`}
                              onClick={() => setShowBasicAdvancedFeatures(!showBasicAdvancedFeatures)}
                            >
                              🧠 Proces myślenia
                              {showBasicAdvancedFeatures ? '▼' : '▶'}
                            </button>
                            {showBasicAdvancedFeatures && (
                              <div className="feature-content thinking-content">
                                <pre>{basicAnswer.thinking_process}</pre>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {basicAnswer?.web_search_queries && basicAnswer.web_search_queries.length > 0 && (
                          <div className="feature-section">
                            <button 
                              className={`feature-toggle ${showBasicAdvancedFeatures ? 'expanded' : ''}`}
                              onClick={() => setShowBasicAdvancedFeatures(!showBasicAdvancedFeatures)}
                            >
                              🌐 Wyszukiwanie internetowe
                              {showBasicAdvancedFeatures ? '▼' : '▶'}
                            </button>
                            {showBasicAdvancedFeatures && (
                              <div className="feature-content web-search-content">
                                <div className="search-queries">
                                  <h5>Zapytania:</h5>
                                  <ul>
                                    {basicAnswer.web_search_queries.map((query, index) => (
                                      <li key={index}>{query}</li>
                                    ))}
                                  </ul>
                                </div>
                                {basicAnswer?.web_search_results && basicAnswer.web_search_results.length > 0 && (
                                  <div className="search-results">
                                    <h5>Wyniki:</h5>
                                    <ul>
                                      {basicAnswer.web_search_results.map((result, index) => (
                                        <li key={index}>{result}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {basicAnswer?.tool_calls && basicAnswer.tool_calls.length > 0 && (
                          <div className="feature-section">
                            <button 
                              className={`feature-toggle ${showBasicAdvancedFeatures ? 'expanded' : ''}`}
                              onClick={() => setShowBasicAdvancedFeatures(!showBasicAdvancedFeatures)}
                            >
                              🔧 Wywołania narzędzi
                              {showBasicAdvancedFeatures ? '▼' : '▶'}
                            </button>
                            {showBasicAdvancedFeatures && (
                              <div className="feature-content tool-calls-content">
                                <ul>
                                  {basicAnswer.tool_calls.map((call, index) => (
                                    <li key={index}>{call}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {basicAnswer?.confidence_level && (
                          <div className="confidence-indicator">
                            <span className="confidence-label">📊 Poziom pewności:</span>
                            <span className="confidence-value">{basicAnswer.confidence_level}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* RAG Sources Display */}
                {chatResponse.rag_sources && parseSources(chatResponse.rag_sources).length > 0 && (
                  <div className="rag-sources">
                    <h5><FontAwesomeIcon icon={faInfoCircle} /> Źródła wykorzystane w odpowiedzi:</h5>
                    <ul className="sources-list" role="region" aria-label="Źródła wykorzystane w odpowiedzi">
                      {parseSources(chatResponse.rag_sources).map((source) => (
                        <li key={source.id} className="source-item">
                          <span className="source-number">[{source.id}]</span>
                          <span className="source-title">{source.title}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="response-meta">
                  <small>Czas odpowiedzi: {chatResponse.response_time_ms}ms</small>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Clinical Improvement Tab */}
      {activeTab === 'clinical-improvement' && (
        <div className="tab-content">
          <div className="toolbox-section">
            <h3><FontAwesomeIcon icon={faMagic} /> Ulepszanie kontekstu klinicznego</h3>
            <p>Użyj AI do poprawy i uzupełnienia informacji klinicznych</p>

            {/* Current Context */}
            <div className="form-group">
              <label>Obecny kontekst kliniczny:</label>
              <textarea
                value={currentContext}
                onChange={(e) => setCurrentContext(e.target.value)}
                placeholder="Wprowadź obecny kontekst kliniczny do ulepszenia..."
                rows={12}
                disabled={improvementLoading}
              />
            </div>

            {/* Improvement Request */}
            <div className="form-group">
              <label>Automatyczne ulepszenie:</label>
              <p className="info-text">
                System automatycznie poprawi błędy językowe, rozwinie skróty, 
                skoryguje klasyfikacje TNM i doda podsumowania zgodnie ze standardami medycznymi.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                onClick={handleClinicalImprovement}
                disabled={improvementLoading || !currentContext.trim()}
                className="primary-button"
              >
                {improvementLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Ulepszanie...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagic} />
                    Ulepsz kontekst
                  </>
                )}
              </button>
              <button
                onClick={resetImprovementForm}
                disabled={improvementLoading}
                className="secondary-button"
              >
                <FontAwesomeIcon icon={faTimes} />
                Wyczyść
              </button>
            </div>

            {/* Improvement Result */}
            {improvementResponse && (
              <div className="improvement-result">
                <h4>Ulepszony kontekst kliniczny:</h4>
                <div className="improved-context">
                  <div className="context-header">
                    <button
                      onClick={() => speakText(improvementResponse.improved_context)}
                      disabled={isSpeaking}
                      className="speak-button"
                      title="Odczytaj ulepszony kontekst"
                    >
                      <FontAwesomeIcon icon={faVolumeUp} />
                    </button>
                    <button
                      onClick={() => navigator.clipboard.writeText(improvementResponse.improved_context)}
                      className="copy-button"
                      title="Skopiuj do schowka"
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                  </div>
                  <div className="context-content">
                    <ReactMarkdown 
                      components={{
                        p: ({children}) => <p>{children}</p>,
                        br: () => <br />,
                      }}
                    >
                      {improvementResponse.improved_context}
                    </ReactMarkdown>
                  </div>
                </div>
                <div className="improvement-meta">
                  <small>Czas przetwarzania: {improvementResponse.response_time_ms}ms</small>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="tab-content">
          <div className="toolbox-section">
            <h3><FontAwesomeIcon icon={faHistory} /> Historia użycia narzędzi</h3>
            <p>{user?.is_admin ? 'Historia wszystkich użytkowników' : 'Twoja historia użycia'}</p>

            {historyLoading ? (
              <div className="loading-state">
                <FontAwesomeIcon icon={faSpinner} spin />
                Ładowanie historii...
              </div>
            ) : history.length === 0 ? (
              <div className="empty-state">
                <FontAwesomeIcon icon={faHistory} />
                <p>Brak historii użycia narzędzi</p>
              </div>
            ) : (
              <div className="history-list">
                {history.map((item) => (
                  <div key={item.id} className="history-item">
                    <div className="history-header">
                      <span className="tool-type">
                        <FontAwesomeIcon icon={item.tool_type === 'ai_chat' ? faComments : faMagic} />
                        {item.tool_type === 'ai_chat' ? 'Chat AI' : 'Ulepszanie kontekstu'}
                      </span>
                      <span className="timestamp">{formatDate(item.created_at)}</span>
                      <span className="ai-provider">{item.ai_provider}</span>
                    </div>
                    <div className="history-content">
                      <div className="question">
                        <strong>Pytanie/Prośba:</strong> {item.question}
                      </div>
                      {item.clinical_context && (
                        <div className="clinical-context">
                          <strong>Kontekst kliniczny:</strong>
                          <div className="context-content">
                            <ReactMarkdown 
                              components={{
                                p: ({children}) => <p>{children}</p>,
                                br: () => <br />,
                              }}
                            >
                              {item.clinical_context}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}
                      {item.enhanced_question && (
                        <div className="enhanced-question">
                          <strong>Ulepszone pytanie:</strong> {item.enhanced_question}
                        </div>
                      )}
                      {item.patient_name && (
                        <div className="patient-info">
                          <strong>Pacjent:</strong> {item.patient_name} ({item.patient_email})
                        </div>
                      )}
                      {item.rag_answer && (
                        <div className="answer rag-answer">
                          <strong>Odpowiedź RAG:</strong> 
                          <div className="answer-content">
                            <ReactMarkdown 
                              components={{
                                p: ({children}) => <p>{children}</p>,
                                br: () => <br />,
                              }}
                            >
                              {item.rag_answer}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}
                      {item.basic_answer && (
                        <div className="answer basic-answer">
                          <strong>Odpowiedź AI:</strong>
                          <div className="answer-content">
                            <ReactMarkdown 
                              components={{
                                p: ({children}) => <p>{children}</p>,
                                br: () => <br />,
                              }}
                            >
                              {item.basic_answer}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}
                      <div className="history-meta">
                        {item.selected_answer && (
                          <span className={`selection-badge ${item.selected_answer}`}>
                            <FontAwesomeIcon icon={faCheck} />
                            Wybrano: {item.selected_answer === 'rag' ? 'RAG' : 'AI'}
                          </span>
                        )}
                        <span className="response-time">
                          Czas: {item.response_time_ms}ms
                        </span>
                      </div>
                      {(item.feedback || item.additional_data?.feedback) && (
                        <div className="feedback-display">
                          <strong>Opinia:</strong>
                          <div className="feedback-content">{item.feedback || item.additional_data?.feedback}</div>
                        </div>
                      )}
                      {/* RAG Sources preview (from history additional_data) */}
                      {item.additional_data?.rag_sources_preview && (
                        <details className="rag-sources-preview">
                          <summary><FontAwesomeIcon icon={faInfoCircle} /> Źródła RAG (skrót)</summary>
                          <div className="sources-content" role="region" aria-label="Skrót źródeł RAG" tabIndex={0}>
                            <code>{item.additional_data.rag_sources_preview}</code>
                          </div>
                        </details>
                      )}
                      {/* Logged prompts for transparency/debugging */}
                      {(item.rag_prompt || item.basic_prompt) && (
                        <details className="prompts-details">
                          <summary>Prompty użyte przez AI</summary>
                          {item.rag_prompt && (
                            <div className="prompt-section">
                              <strong>Prompt RAG:</strong>
                              <div className="prompt-content">
                                <pre>{item.rag_prompt}</pre>
                              </div>
                            </div>
                          )}
                          {item.basic_prompt && (
                            <div className="prompt-section">
                              <strong>Prompt podstawowy:</strong>
                              <div className="prompt-content">
                                <pre>{item.basic_prompt}</pre>
                              </div>
                            </div>
                          )}
                        </details>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Statistics Tab (Admin only) */}
      {activeTab === 'statistics' && user?.is_admin && (
        <div className="tab-content">
          <div className="toolbox-section">
            <h3><FontAwesomeIcon icon={faChartBar} /> Statystyki użycia narzędzi</h3>
            <p>Analiza użycia narzędzi AI w ciągu ostatnich 30 dni</p>

            {statisticsLoading ? (
              <div className="loading-state">
                <FontAwesomeIcon icon={faSpinner} spin />
                Ładowanie statystyk...
              </div>
            ) : statistics ? (
              <div className="statistics-grid">
                <div className="stat-card">
                  <h4>Całkowita liczba sesji</h4>
                  <div className="stat-value">{statistics.total_sessions}</div>
                </div>

                <div className="stat-card">
                  <h4>Średni czas odpowiedzi</h4>
                  <div className="stat-value">{statistics.average_response_time_ms}ms</div>
                </div>

                <div className="stat-card">
                  <h4>Rodzaje narzędzi</h4>
                  <div className="breakdown">
                    {Object.entries(statistics.tool_type_breakdown).map(([type, count]) => (
                      <div key={type} className="breakdown-item">
                        <span>{type === 'ai_chat' ? 'Chat AI' : 'Ulepszanie'}</span>
                        <span>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="stat-card">
                  <h4>Preferencje odpowiedzi</h4>
                  <div className="breakdown">
                    {Object.entries(statistics.answer_preferences).map(([type, count]) => (
                      <div key={type} className="breakdown-item">
                        <span>{type === 'rag' ? 'RAG' : 'AI'}</span>
                        <span>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {statistics.top_users.length > 0 && (
                  <div className="stat-card full-width">
                    <h4>Najaktywniejsze użytkownicy</h4>
                    <div className="top-users">
                      {statistics.top_users.map((user) => (
                        <div key={user.user_id} className="user-item">
                          <span>{user.name}</span>
                          <span>{user.usage_count} sesji</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state">
                <FontAwesomeIcon icon={faChartBar} />
                <p>Brak dostępnych statystyk</p>
              </div>
            )}
          </div>
        </div>
      )}

  {/* Feedback Popup */}
      {/* Opinion Modal for Toolbox */}
      <OpinionModal
        isOpen={showFeedback}
        onClose={handleSkipFeedback}
        onSubmit={handleSubmitFeedback}
        title="Dodaj opinię o odpowiedzi"
        description="Czy odpowiedź była pomocna? Podziel się swoją opinią aby pomóc nam ulepszać narzędzia dla lekarzy."
        placeholder="Napisz swoją opinię o jakości odpowiedzi..."
        submitButtonText="Zapisz opinię"
        skipButtonText="Pomiń"
        maxLength={1000}
      />
    </div>
  );
};
